import React, { useState, useEffect } from 'react';
import { Search, Package, Plus, Minus, Save, AlertTriangle, ArrowLeft, Trash2, Upload, Link2, RefreshCw, Truck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import { getProxiedImageUrl } from '../utils/helpers';
import ImportFromUrlModal from '../components/ImportFromUrlModal';
import ImportFromSupplierModal from '../components/ImportFromSupplierModal';
import PwaStatsWidget from '../components/PwaStatsWidget';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInventory = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [stockChanges, setStockChanges] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {sku, name} for delete confirmation modal
  const [updatingTag, setUpdatingTag] = useState(null); // SKU being updated
  const [editingProduct, setEditingProduct] = useState(null); // Product being edited in modal
  const [editForm, setEditForm] = useState({});
  const [imageInput, setImageInput] = useState(''); // For URL input
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'imported' | 'manual'
  const [stockFilter, setStockFilter] = useState('all'); // 'all' | 'out' | 'low'
  const [refreshing, setRefreshing] = useState({}); // SKU -> bool
  const [selectedSkus, setSelectedSkus] = useState(new Set()); // SKUs selected for bulk operations
  const [bulkStockValue, setBulkStockValue] = useState(''); // value to apply in bulk
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh when the admin tab is re-focused after being hidden for 2+ min
  // Fixes "no products found if interface is left idle" bug
  useEffect(() => {
    let lastHiddenAt = null;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAt = Date.now();
        return;
      }
      if (document.visibilityState === 'visible') {
        const awayMs = lastHiddenAt ? Date.now() - lastHiddenAt : 0;
        // Refresh if the tab was away >2 minutes OR the product list is unexpectedly empty
        if (awayMs > 2 * 60 * 1000 || products.length === 0) {
          fetchProducts();
        }
        lastHiddenAt = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length]);

  useEffect(() => {
    filterProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, searchTerm, selectedCategory, sourceFilter, stockFilter]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      // Defensive: ensure we got an array before overwriting state.
      // Prevents the list from going empty if the API returned an error object
      // (e.g. {detail: "..."} on 401/500) which previously caused the
      // "no products found until refresh" bug.
      if (!Array.isArray(data)) {
        console.warn('[AdminInventory] /api/products non-array response:', data);
        toast.error(language === 'it' ? 'Risposta non valida, riprova' : 'Invalid response, try again');
        setLoading(false);
        return;
      }
      setProducts(data);
      setLoading(false);
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel caricamento prodotti' : 'Error loading products');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
    }
  };

  const filterProducts = () => {
    let filtered = [...products];

    // Filter by search
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.name_it.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.brand && p.brand.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filter by category
    if (selectedCategory && selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    // Filter by source (imported from URL vs manual)
    if (sourceFilter === 'imported') {
      filtered = filtered.filter(p => p.source_url || p.ebay_item_id);
    } else if (sourceFilter === 'manual') {
      filtered = filtered.filter(p => !p.source_url && !p.ebay_item_id);
    }

    // Filter by stock level (driven by clickable stat cards)
    if (stockFilter === 'out') {
      filtered = filtered.filter(p => (p.stock || 0) === 0);
    } else if (stockFilter === 'low') {
      filtered = filtered.filter(p => (p.stock || 0) > 0 && (p.stock || 0) < 5);
    }

    setFilteredProducts(filtered);
  };

  const handleStockChange = (sku, newStock) => {
    setStockChanges(prev => ({
      ...prev,
      [sku]: parseInt(newStock) || 0
    }));
  };

  // ---- Bulk selection / bulk-stock helpers ----
  const toggleSelectSku = (sku) => {
    setSelectedSkus(prev => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku); else next.add(sku);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedSkus(prev => {
      const visibleSkus = filteredProducts.map(p => p.sku);
      const allSelected = visibleSkus.length > 0 && visibleSkus.every(s => prev.has(s));
      if (allSelected) {
        // Deselect all visible
        const next = new Set(prev);
        visibleSkus.forEach(s => next.delete(s));
        return next;
      }
      // Add all visible
      return new Set([...prev, ...visibleSkus]);
    });
  };

  const clearSelection = () => {
    setSelectedSkus(new Set());
    setBulkStockValue('');
  };

  const applyBulkStock = async () => {
    if (selectedSkus.size === 0) {
      toast.error(language === 'it' ? 'Nessun prodotto selezionato' : 'No product selected');
      return;
    }
    const value = parseInt(bulkStockValue);
    if (Number.isNaN(value) || value < 0) {
      toast.error(language === 'it' ? 'Inserisci una quantità valida (≥ 0)' : 'Enter a valid quantity (≥ 0)');
      return;
    }
    setBulkSaving(true);
    try {
      const updates = Array.from(selectedSkus).map(sku => ({ sku, stock: value }));
      // Use axios explicitly: a global `fetch` interceptor on this app
      // re-reads the response body and causes "Body has already been consumed"
      // when we call `await res.json()` directly.
      const { data } = await axios.post(`${API_URL}/api/products/bulk-stock`, updates);

      toast.success(
        language === 'it'
          ? `Stock aggiornato a ${value} su ${data.updated || selectedSkus.size} prodotti`
          : `Stock set to ${value} on ${data.updated || selectedSkus.size} products`
      );
      await fetchProducts();
      clearSelection();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || (language === 'it' ? 'Errore aggiornamento massivo' : 'Bulk update error');
      toast.error(msg);
    } finally {
      setBulkSaving(false);
    }
  };

  const refreshProductFromEbay = async (sku) => {
    setRefreshing(prev => ({ ...prev, [sku]: true }));
    try {
      const res = await fetch(`${API_URL}/api/admin/refresh-from-ebay/${encodeURIComponent(sku)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ update_price: true, update_stock: true, update_description: true, update_images: false }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Refresh failed');

      const changedCount = (data.changed || []).length;
      if (changedCount === 0) {
        toast.info(language === 'it' ? 'Già aggiornato (nessuna modifica)' : language === 'es' ? 'Ya actualizado (sin cambios)' : 'Already up to date');
      } else {
        toast.success(
          (language === 'it' ? `Aggiornati ${changedCount} campi: ` : language === 'es' ? `Actualizados ${changedCount} campos: ` : `Updated ${changedCount} fields: `)
          + (data.changed || []).join(', ')
        );
        await fetchProducts();
      }
    } catch (e) {
      toast.error(e.message || (language === 'it' ? "Errore durante l'aggiornamento" : 'Refresh error'));
    } finally {
      setRefreshing(prev => ({ ...prev, [sku]: false }));
    }
  };


  const incrementStock = (sku, currentStock) => {
    const newStock = (stockChanges[sku] !== undefined ? stockChanges[sku] : currentStock) + 1;
    setStockChanges(prev => ({
      ...prev,
      [sku]: newStock
    }));
  };

  const decrementStock = (sku, currentStock) => {
    const currentValue = stockChanges[sku] !== undefined ? stockChanges[sku] : currentStock;
    const newStock = Math.max(0, currentValue - 1);
    setStockChanges(prev => ({
      ...prev,
      [sku]: newStock
    }));
  };

  const saveStock = async (sku) => {
    const newStock = stockChanges[sku];
    if (newStock === undefined) return;

    try {
      const response = await fetch(`${API_URL}/api/products/${sku}/stock?stock=${newStock}`, {
        method: 'PATCH',
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Stock aggiornato: ${data.old_stock} → ${data.new_stock}`);
        
        // Update local state
        setProducts(prev => prev.map(p => 
          p.sku === sku ? { ...p, stock: newStock } : p
        ));
        
        // Clear change
        setStockChanges(prev => {
          const newChanges = { ...prev };
          delete newChanges[sku];
          return newChanges;
        });
      } else {
        toast.error('Errore aggiornamento stock');
      }
    } catch (error) {
      toast.error('Errore connessione');
    }
  };

  const saveAllChanges = async () => {
    const updates = Object.entries(stockChanges).map(([sku, stock]) => ({
      sku,
      stock
    }));

    if (updates.length === 0) {
      toast.info('Nessuna modifica da salvare');
      return;
    }

    try {
      // Use axios: a global fetch interceptor causes "Body has already been
      // consumed" when calling `await response.json()` directly.
      const { data } = await axios.post(`${API_URL}/api/products/bulk-stock`, updates);

      if (data.success) {
        toast.success(`${data.updated} prodotti aggiornati!`);

        // Update local state
        setProducts(prev => prev.map(p => {
          const newStock = stockChanges[p.sku];
          return newStock !== undefined ? { ...p, stock: newStock } : p;
        }));

        setStockChanges({});
      }
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Errore aggiornamento multiplo';
      toast.error(msg);
    }
  };

  const updateProductTag = async (sku, tag) => {
    setUpdatingTag(sku);
    try {
      const response = await fetch(`${API_URL}/api/products/${sku}/tag`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag: tag === 'nessuno' ? null : tag })
      });

      const data = await response.json();
      
      if (data.success) {
        // Update local state
        setProducts(prev => prev.map(p => 
          p.sku === sku ? { ...p, tag: tag === 'nessuno' ? null : tag } : p
        ));
        
        const tagLabel = tag === 'nessuno' ? 'rimosso' : tag;
        toast.success(`Tag aggiornato: ${tagLabel}`, { duration: 2000 });
      } else {
        toast.error('Errore aggiornamento tag');
      }
    } catch (error) {
      toast.error('Errore aggiornamento tag');
    } finally {
      setUpdatingTag(null);
    }
  };

  const handleDeleteProduct = async (sku) => {
    try {
      const response = await fetch(`${API_URL}/api/products/${sku}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Prodotto ${sku} eliminato!`);
        
        // Remove from local state
        setProducts(prev => prev.filter(p => p.sku !== sku));
        setDeleteConfirm(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore eliminazione prodotto');
      }
    } catch (error) {
      toast.error('Errore connessione');
    }
  };


  // Edit Product Modal Functions
  const openEditModal = (product) => {
    setEditingProduct(product);
    setEditForm({
      name_it: product.name_it || '',
      name_en: product.name_en || '',
      description_it: product.description_it || '',
      description_en: product.description_en || '',
      price: product.price || 0,
      stock: product.stock || 0,
      category: product.category || '',
      brand: product.brand || '',
      images: product.images || [],
      compatible_models: Array.isArray(product.compatible_models)
        ? product.compatible_models.join('\n')
        : '',
    });
    setImageInput('');
  };

  const closeEditModal = () => {
    setEditingProduct(null);
    setEditForm({});
    setImageInput('');
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddImageUrl = () => {
    if (!imageInput.trim()) {
      toast.error(language === 'it' ? 'Inserisci un URL valido' : 'Enter a valid URL');
      return;
    }
    
    setEditForm(prev => ({
      ...prev,
      images: [...(prev.images || []), imageInput.trim()]
    }));
    setImageInput('');
    toast.success(language === 'it' ? 'Immagine aggiunta' : 'Image added');
  };

  const handleRemoveImage = (index) => {
    setEditForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(language === 'it' ? 'Seleziona un file immagine valido' : 'Select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'it' ? 'Immagine troppo grande (max 5MB)' : 'Image too large (max 5MB)');
      return;
    }

    setUploadingImage(true);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditForm(prev => ({
        ...prev,
        images: [...(prev.images || []), reader.result]
      }));
      setUploadingImage(false);
      toast.success(language === 'it' ? 'Immagine caricata' : 'Image uploaded');
    };
    reader.onerror = () => {
      setUploadingImage(false);
      toast.error(language === 'it' ? 'Errore caricamento immagine' : 'Image upload error');
    };
    reader.readAsDataURL(file);
  };

  const saveProductChanges = async () => {
    if (!editingProduct) return;

    try {
      // Convert compatible_models textarea (newline-separated) → array
      const payload = { ...editForm };
      if (typeof payload.compatible_models === 'string') {
        payload.compatible_models = payload.compatible_models
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean);
      }

      const response = await fetch(`${API_URL}/api/products/${editingProduct.sku}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        toast.success(language === 'it' ? 'Prodotto aggiornato!' : 'Product updated!');
        fetchProducts();
        closeEditModal();
      } else {
        toast.error(data.message || 'Errore aggiornamento');
      }
    } catch (error) {
      toast.error(language === 'it' ? 'Errore nel salvataggio' : 'Save error');
    }
  };


  const lowStockCount = products.filter(p => p.stock < 5).length;
  const outOfStockCount = products.filter(p => p.stock === 0).length;
  const pendingChanges = Object.keys(stockChanges).length;

  return (
    <div className="dark-container pt-20 min-h-screen">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-[7.6923%] py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <button 
                onClick={() => navigate('/admin/import')}
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              >
                <ArrowLeft size={20} />
                {language === 'it' ? 'Torna a Import' : 'Back to Import'}
              </button>
              <button 
                onClick={() => navigate('/admin/orders')}
                className="btn-secondary text-sm py-2 px-4"
              >
                📦 {language === 'it' ? 'Vedi Ordini' : 'View Orders'}
              </button>
              <button 
                onClick={() => navigate('/admin/newsletter')}
                className="btn-secondary text-sm py-2 px-4"
              >
                📧 {language === 'it' ? 'Newsletter' : 'Newsletter'}
              </button>
              <button 
                onClick={() => navigate('/admin/push')}
                className="btn-secondary text-sm py-2 px-4"
                data-testid="admin-push-link"
              >
                🔔 {language === 'it' ? 'Notifiche Push' : 'Push'}
              </button>
              <button 
                onClick={() => navigate('/admin/coupons')}
                className="btn-secondary text-sm py-2 px-4"
              >
                🏷️ {language === 'it' ? 'Coupon' : 'Coupons'}
              </button>
              <button 
                onClick={() => setShowImportModal(true)}
                className="btn-primary text-sm py-2 px-4 flex items-center gap-2"
                data-testid="import-from-url-btn"
              >
                <Link2 size={16} />
                {language === 'it' ? 'Aggiungi da URL' : language === 'es' ? 'Añadir desde URL' : 'Add from URL'}
              </button>
              <button
                onClick={() => setShowSupplierModal(true)}
                className="btn-primary text-sm py-2 px-4 flex items-center gap-2"
                data-testid="import-from-supplier-btn"
                style={{ background: '#0f172a', border: '1px solid #FF6600', color: '#FF6600' }}
              >
                <Truck size={16} />
                {language === 'it' ? 'Importa da Fornitore' : language === 'es' ? 'Importar de Proveedor' : 'Import from Supplier'}
              </button>
              <button
                onClick={async () => {
                  const preview = window.confirm(
                    language === 'it'
                      ? 'Anteprima (dry-run): scansiono tutti i prodotti e ti mostro quanti hanno una lista moto rimasta nella descrizione, senza modificare nulla. Procedo?'
                      : 'Dry-run preview: I will scan every product and show how many have a bike list in their description, without changing anything. Proceed?'
                  );
                  if (!preview) return;
                  try {
                    const r1 = await axios.post(`${API_URL}/api/admin/backfill-compatible-models?dry_run=true`);
                    const { scanned, matched, preview: sample } = r1.data;
                    const proceed = window.confirm(
                      (language === 'it'
                        ? `Scansionati ${scanned} prodotti — trovate liste da migrare su ${matched}.\n\n`
                        : `Scanned ${scanned} products — ${matched} eligible for migration.\n\n`) +
                      (sample.slice(0, 5).map(s => `• ${s.sku}: ${s.bikes_found.length} moto`).join('\n')) +
                      (language === 'it'
                        ? `\n\nProcedo con la migrazione reale?`
                        : `\n\nProceed with the actual migration?`)
                    );
                    if (!proceed) return;
                    const r2 = await axios.post(`${API_URL}/api/admin/backfill-compatible-models`);
                    toast.success(
                      language === 'it'
                        ? `Aggiornati ${r2.data.updated} prodotti`
                        : `Updated ${r2.data.updated} products`
                    );
                    fetchProducts();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || e.message);
                  }
                }}
                className="text-sm py-2 px-4 flex items-center gap-2"
                data-testid="backfill-compatible-models-btn"
                style={{ background: '#111', border: '1px solid #FF6600', color: '#FF6600' }}
              >
                🏍️ {language === 'it' ? 'Backfill Compatibilità' : 'Backfill Compatibility'}
              </button>
              <Link
                to="/admin/bike-compatibility"
                className="text-sm py-2 px-4 flex items-center gap-2"
                data-testid="bike-compat-admin-btn"
                style={{ background: '#111', border: '1px solid #FF6600', color: '#FF6600' }}
              >
                🔧 {language === 'it' ? 'Rivedi Compatibilità' : 'Review Compatibility'}
              </Link>
              <button
                onClick={async () => {
                  const preview = window.confirm(
                    language === 'it'
                      ? 'Anteprima (dry-run): scansiono TUTTI i titoli e ti mostro quali verrebbero puliti (rimuovo lista modelli + aggiungo marca). Nessun dato viene modificato ora. Procedo?'
                      : 'Dry-run preview: I will scan every title and show which ones would be cleaned. Proceed?'
                  );
                  if (!preview) return;
                  try {
                    const r1 = await axios.post(`${API_URL}/api/admin/backfill-product-titles?dry_run=true`);
                    const { scanned, changed, preview: sample } = r1.data;
                    const sampleText = sample.slice(0, 8).map(s =>
                      `[${s.sku}]\n  - ${s.before.slice(0, 80)}\n  + ${s.after.slice(0, 80)}`
                    ).join('\n');
                    const proceed = window.confirm(
                      (language === 'it'
                        ? `Scansionati ${scanned} prodotti — verrebbero puliti ${changed} titoli.\n\nEsempi:\n${sampleText}\n\nProcedo con la modifica reale?`
                        : `Scanned ${scanned} — ${changed} titles would be cleaned.\n\nExamples:\n${sampleText}\n\nProceed?`)
                    );
                    if (!proceed) return;
                    const r2 = await axios.post(`${API_URL}/api/admin/backfill-product-titles`);
                    toast.success(
                      language === 'it'
                        ? `Aggiornati ${r2.data.updated} titoli`
                        : `Updated ${r2.data.updated} titles`
                    );
                    fetchProducts();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || e.message);
                  }
                }}
                className="text-sm py-2 px-4 flex items-center gap-2"
                data-testid="backfill-titles-btn"
                style={{ background: '#111', border: '1px solid #FF6600', color: '#FF6600' }}
              >
                ✂️ {language === 'it' ? 'Pulisci Titoli' : 'Clean Titles'}
              </button>
              <input
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                id="apps-only-csv-input"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  try {
                    // dry-run first
                    const fd = new FormData();
                    fd.append('file', file);
                    const r1 = await axios.post(
                      `${API_URL}/api/admin/import-applications-only?dry_run=true`,
                      fd,
                      { headers: { 'Content-Type': 'multipart/form-data' } },
                    );
                    const {
                      csv_rows_with_bikes,
                      matched_in_db,
                      not_found_count,
                      preview,
                    } = r1.data;
                    const previewText = preview.slice(0, 5).map(p =>
                      `[${p.sku}] ${p.name.slice(0, 40)}  → ${p.after.slice(0, 2).join(', ')}${p.after.length > 2 ? '...' : ''}`
                    ).join('\n');
                    const proceed = window.confirm(
                      language === 'it'
                        ? `Anteprima:\n• ${csv_rows_with_bikes} righe con applicazioni nel CSV\n• ${matched_in_db} corrispondono a prodotti in DB (verranno aggiornati)\n• ${not_found_count} SKU non trovati (verranno ignorati)\n\nEsempi:\n${previewText}\n\nProcedo con l'aggiornamento?`
                        : `Preview:\n• ${csv_rows_with_bikes} CSV rows with applications\n• ${matched_in_db} match DB products (will be updated)\n• ${not_found_count} SKUs not found\n\nExamples:\n${previewText}\n\nProceed?`
                    );
                    if (!proceed) return;
                    const fd2 = new FormData();
                    fd2.append('file', file);
                    const r2 = await axios.post(
                      `${API_URL}/api/admin/import-applications-only`,
                      fd2,
                      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
                    );
                    toast.success(
                      language === 'it'
                        ? `Aggiornati ${r2.data.updated} prodotti con applicazioni`
                        : `Updated ${r2.data.updated} products with applications`
                    );
                    fetchProducts();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || err.message);
                  }
                }}
              />
              <button
                onClick={() => document.getElementById('apps-only-csv-input')?.click()}
                className="text-sm py-2 px-4 flex items-center gap-2"
                data-testid="import-applications-only-btn"
                style={{ background: '#111', border: '1px solid #FF6600', color: '#FF6600' }}
              >
                🎯 {language === 'it' ? 'Import Applicazioni CSV' : 'Import Applications CSV'}
              </button>
              <button 
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${API_URL}/api/export/csv`;
                  link.download = 'allmx_products_export.csv';
                  link.click();
                  toast.success(language === 'it' ? 'Download CSV avviato' : 'CSV download started');
                }}
                className="btn-primary text-sm py-2 px-4"
                data-testid="export-csv-btn"
              >
                {language === 'it' ? 'Esporta CSV' : 'Export CSV'}
              </button>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">
              {language === 'it' ? 'Gestione Inventario' : 'Inventory Management'}
            </h1>
            
            {/* Bulk Update Link */}
            <Link

              to="/admin/inventory-bulk"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-semibold transition-colors"
            >
              <Upload size={18} />
              {language === 'it' ? 'Aggiornamento Massivo CSV' : 'Bulk CSV Update'}
            </Link>
          </div>
          
          {pendingChanges > 0 && (
            <button onClick={saveAllChanges} className="btn-primary flex items-center gap-2">
              <Save size={20} />
              {language === 'it' ? `Salva ${pendingChanges} Modifiche` : `Save ${pendingChanges} Changes`}
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Package size={24} className="text-primary" />
                <div>
                  <div className="text-2xl font-bold" data-testid="total-products-count">{products.length}</div>
                  <div className="text-sm text-white/60">
                    {language === 'it' ? 'Prodotti Totali' : 'Total Products'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setLoading(true); fetchProducts(); fetchCategories(); }}
                className="p-2 rounded hover:bg-white/10 transition-colors text-white/70 hover:text-primary"
                title={language === 'it' ? 'Ricarica elenco prodotti' : 'Reload products'}
                data-testid="refresh-products-btn"
                aria-label="refresh"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setStockFilter(stockFilter === 'out' ? 'all' : 'out')}
            className={`bg-red-500/10 rounded-lg p-4 border text-left transition-all hover:bg-red-500/20 ${stockFilter === 'out' ? 'border-red-500 ring-2 ring-red-500/40' : 'border-red-500/30'}`}
            title={language === 'it' ? 'Clicca per filtrare i prodotti esauriti' : 'Click to filter out-of-stock products'}
            data-testid="filter-out-of-stock"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle size={24} className="text-red-500" />
              <div>
                <div className="text-2xl font-bold text-red-500">{outOfStockCount}</div>
                <div className="text-sm text-white/60">
                  {language === 'it' ? 'Esauriti' : 'Out of Stock'}
                  {stockFilter === 'out' && <span className="ml-2 text-red-400">✓</span>}
                </div>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setStockFilter(stockFilter === 'low' ? 'all' : 'low')}
            className={`bg-yellow-500/10 rounded-lg p-4 border text-left transition-all hover:bg-yellow-500/20 ${stockFilter === 'low' ? 'border-yellow-500 ring-2 ring-yellow-500/40' : 'border-yellow-500/30'}`}
            title={language === 'it' ? 'Clicca per filtrare i prodotti con stock basso' : 'Click to filter low-stock products'}
            data-testid="filter-low-stock"
          >
            <div className="flex items-center gap-3">
              <AlertTriangle size={24} className="text-yellow-500" />
              <div>
                <div className="text-2xl font-bold text-yellow-500">{lowStockCount}</div>
                <div className="text-sm text-white/60">
                  {language === 'it' ? 'Stock Basso (< 5)' : 'Low Stock (< 5)'}
                  {stockFilter === 'low' && <span className="ml-2 text-yellow-400">✓</span>}
                </div>
              </div>
            </div>
          </button>

          <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
            <div className="flex items-center gap-3">
              <Save size={24} className="text-blue-500" />
              <div>
                <div className="text-2xl font-bold text-blue-500">{pendingChanges}</div>
                <div className="text-sm text-white/60">
                  {language === 'it' ? 'Modifiche Pending' : 'Pending Changes'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PWA Stats Widget */}
        <div className="mb-6">
          <PwaStatsWidget />
        </div>

        {/* Filters */}
        <div className="bg-white/5 rounded-lg p-6 border border-white/10 mb-6">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm mb-2">
                {language === 'it' ? 'Cerca Prodotto' : language === 'es' ? 'Buscar Producto' : 'Search Product'}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={language === 'it' ? 'SKU, nome, marca...' : 'SKU, name, brand...'}
                  className="w-full bg-black/30 border border-white/20 rounded pl-10 pr-4 py-2 focus:outline-none focus:border-primary"
                  data-testid="inventory-search"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm mb-2">
                {language === 'it' ? 'Categoria' : language === 'es' ? 'Categoría' : 'Category'}
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                data-testid="inventory-category-filter"
              >
                <option value="all">{language === 'it' ? 'Tutte le Categorie' : language === 'es' ? 'Todas las Categorías' : 'All Categories'}</option>
                {categories.map(cat => (
                  <option key={cat.name} value={cat.name}>
                    {cat.name} ({cat.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Source Filter */}
            <div>
              <label className="block text-sm mb-2">
                {language === 'it' ? 'Origine' : language === 'es' ? 'Origen' : 'Source'}
              </label>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                data-testid="inventory-source-filter"
              >
                <option value="all">{language === 'it' ? 'Tutti i prodotti' : language === 'es' ? 'Todos los productos' : 'All products'}</option>
                <option value="imported">
                  {language === 'it' ? 'Importati da URL eBay' : language === 'es' ? 'Importados desde URL eBay' : 'Imported from eBay URL'}
                </option>
                <option value="manual">
                  {language === 'it' ? 'Inseriti manualmente / CSV' : language === 'es' ? 'Insertados manualmente / CSV' : 'Manual / CSV'}
                </option>
              </select>
            </div>
          </div>

          {/* Active filter summary */}
          {sourceFilter !== 'all' && (
            <div className="mt-4 text-sm text-white/70" data-testid="filter-summary">
              {sourceFilter === 'imported' ? '🔗 ' : '📝 '}
              {language === 'it'
                ? `Mostrando ${filteredProducts.length} prodotti ${sourceFilter === 'imported' ? 'importati da eBay' : 'inseriti manualmente'}`
                : language === 'es'
                ? `Mostrando ${filteredProducts.length} productos ${sourceFilter === 'imported' ? 'importados desde eBay' : 'insertados manualmente'}`
                : `Showing ${filteredProducts.length} ${sourceFilter === 'imported' ? 'imported from eBay' : 'manual'} products`}
              <button
                onClick={() => setSourceFilter('all')}
                className="ml-2 text-[#FF6600] hover:underline"
                data-testid="clear-source-filter"
              >
                ✕ {language === 'it' ? 'rimuovi filtro' : language === 'es' ? 'quitar filtro' : 'clear filter'}
              </button>
            </div>
          )}
        </div>

        {/* Bulk Selection Toolbar — visible when at least one product is selected */}
        {selectedSkus.size > 0 && (
          <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="bulk-toolbar">
            <span className="text-sm font-semibold">
              {language === 'it'
                ? `${selectedSkus.size} prodotti selezionati`
                : `${selectedSkus.size} products selected`}
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <label className="text-sm text-white/70">
                {language === 'it' ? 'Quantità:' : 'Quantity:'}
              </label>
              <input
                type="number"
                min="0"
                value={bulkStockValue}
                onChange={(e) => setBulkStockValue(e.target.value)}
                placeholder="0"
                className="w-24 bg-black/30 border border-white/20 rounded px-3 py-1.5 focus:outline-none focus:border-primary"
                data-testid="bulk-stock-input"
              />
              <button
                type="button"
                onClick={applyBulkStock}
                disabled={bulkSaving || bulkStockValue === ''}
                className="btn-primary px-4 py-1.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bulk-apply-btn"
              >
                {bulkSaving
                  ? (language === 'it' ? 'Salvataggio...' : 'Saving...')
                  : (language === 'it' ? 'Applica a Selezionati' : 'Apply to Selected')}
              </button>
              <button
                type="button"
                onClick={clearSelection}
                className="px-3 py-1.5 text-sm text-white/70 hover:text-white hover:bg-white/10 rounded transition-colors"
                data-testid="bulk-clear-btn"
              >
                {language === 'it' ? 'Annulla' : 'Cancel'}
              </button>
            </div>
          </div>
        )}

        {/* Products Table */}
        <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
          {/* Mobile scroll hint */}
          <div className="md:hidden bg-blue-500/10 border-b border-blue-500/30 px-4 py-2 text-sm text-blue-400 text-center">
            {language === 'it' ? '← Scorri orizzontalmente per vedere tutte le colonne →' : '← Scroll horizontally to see all columns →'}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="px-3 py-3 text-center text-sm font-semibold w-10">
                    <input
                      type="checkbox"
                      onChange={toggleSelectAllVisible}
                      checked={filteredProducts.length > 0 && filteredProducts.every(p => selectedSkus.has(p.sku))}
                      title={language === 'it' ? 'Seleziona/Deseleziona tutti i visibili' : 'Toggle all visible'}
                      className="w-4 h-4 cursor-pointer accent-orange-500"
                      data-testid="bulk-select-all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Immagine</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">SKU</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Prodotto</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Marca</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Prezzo</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Stock</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Tag</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Azioni</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Elimina</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-white/60">
                      Caricamento...
                    </td>
                  </tr>
                ) : filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-8 text-center text-white/60">
                      Nessun prodotto trovato
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map(product => {
                    const hasChanges = stockChanges[product.sku] !== undefined;
                    const displayStock = hasChanges ? stockChanges[product.sku] : product.stock;
                    const isLowStock = displayStock < 5 && displayStock > 0;
                    const isOutOfStock = displayStock === 0;

                    return (
                      <tr key={product.sku} className={`border-b border-white/5 hover:bg-white/5 transition-colors ${selectedSkus.has(product.sku) ? 'bg-orange-500/5' : ''}`}>
                        <td className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedSkus.has(product.sku)}
                            onChange={() => toggleSelectSku(product.sku)}
                            className="w-4 h-4 cursor-pointer accent-orange-500"
                            data-testid={`bulk-select-${product.sku}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/products/${product.slug || product.sku}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={language === 'it' ? 'Apri pagina prodotto' : language === 'es' ? 'Abrir página producto' : 'Open product page'}
                            data-testid={`product-image-link-${product.sku}`}
                          >
                            {product.images && product.images[0] ? (
                              <img 
                                src={getProxiedImageUrl(product.images[0])} 
                                alt={product.name_it}
                                className="w-12 h-12 object-cover rounded hover:ring-2 hover:ring-[#FF6600] transition-all cursor-pointer"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-white/10 rounded flex items-center justify-center hover:ring-2 hover:ring-[#FF6600] transition-all cursor-pointer">
                                <Package size={20} className="text-white/40" />
                              </div>
                            )}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-sm font-mono">{product.sku}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div>
                              <Link
                                to={`/products/${product.slug || product.sku}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium hover:text-[#FF6600] hover:underline transition-colors cursor-pointer"
                                title={language === 'it' ? 'Apri pagina prodotto in nuova scheda' : language === 'es' ? 'Abrir página producto en nueva pestaña' : 'Open product page in new tab'}
                                data-testid={`product-name-link-${product.sku}`}
                              >
                                {product.name_it}
                              </Link>
                              <div className="text-xs text-white/60">{product.category}</div>
                            </div>
                            {product.tag && (
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                product.tag === 'offerta' ? 'bg-red-500/20 text-red-400' :
                                product.tag === 'novita' ? 'bg-green-500/20 text-green-400' :
                                product.tag === 'outlet' ? 'bg-yellow-500/20 text-yellow-400' :
                                ''
                              }`}>
                                {product.tag === 'offerta' ? '🏷️ Offerta' :
                                 product.tag === 'novita' ? '✨ Novità' :
                                 product.tag === 'outlet' ? '💰 Outlet' : ''}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">{product.brand || '-'}</td>
                        <td className="px-4 py-3 text-sm font-semibold">€{product.price.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => decrementStock(product.sku, product.stock)}
                              className="p-1 hover:bg-white/10 rounded transition-colors"
                              title="Rimuovi 1"
                            >
                              <Minus size={16} />
                            </button>
                            
                            <input
                              type="number"
                              value={displayStock}
                              onChange={(e) => handleStockChange(product.sku, e.target.value)}
                              className={`w-16 text-center bg-black/30 border rounded px-2 py-1 text-sm focus:outline-none focus:border-primary ${
                                isOutOfStock ? 'border-red-500 text-red-500' :
                                isLowStock ? 'border-yellow-500 text-yellow-500' :
                                hasChanges ? 'border-blue-500 text-blue-500' :
                                'border-white/20'
                              }`}
                              min="0"
                            />
                            
                            <button
                              onClick={() => incrementStock(product.sku, product.stock)}
                              className="p-1 hover:bg-white/10 rounded transition-colors"
                              title="Aggiungi 1"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <select
                            value={product.tag || 'nessuno'}
                            onChange={(e) => updateProductTag(product.sku, e.target.value)}
                            disabled={updatingTag === product.sku}
                            className="bg-black/30 border border-white/20 rounded px-3 py-1 text-sm focus:outline-none focus:border-primary disabled:opacity-50"
                          >
                            <option value="nessuno">Nessuno</option>
                            <option value="offerta">🏷️ Offerta</option>
                            <option value="novita">✨ Novità</option>
                            <option value="outlet">💰 Outlet</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {/* Refresh from eBay button — only for imported products */}
                            {(product.source_url || product.ebay_item_id) && (
                              <button
                                onClick={() => refreshProductFromEbay(product.sku)}
                                disabled={refreshing[product.sku]}
                                className="p-2 hover:bg-orange-500/20 text-[#FF6600] rounded transition-colors disabled:opacity-50"
                                title={language === 'it' ? 'Aggiorna prezzo, stock e descrizione da eBay' : language === 'es' ? 'Actualizar desde eBay' : 'Refresh from eBay'}
                                data-testid={`refresh-ebay-${product.sku}`}
                              >
                                <RefreshCw size={16} className={refreshing[product.sku] ? 'animate-spin' : ''} />
                              </button>
                            )}

                            {/* Edit Button */}
                            <button
                              onClick={() => openEditModal(product)}
                              className="p-2 hover:bg-blue-500/20 text-blue-400 rounded transition-colors"
                              title={language === 'it' ? 'Modifica prodotto' : 'Edit product'}
                            >
                              ✏️
                            </button>
                            
                            {/* Save Stock Button */}
                            {hasChanges && (
                              <button
                                onClick={() => saveStock(product.sku)}
                                className="btn-primary text-xs py-1 px-3"
                                title="Salva modifiche"
                              >
                                <Save size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setDeleteConfirm({ sku: product.sku, name: product.name_it })}
                            className="p-2 hover:bg-red-500/20 text-red-500 rounded transition-colors"
                            title="Elimina prodotto"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 text-sm text-white/60 text-center">
          {language === 'it' 
            ? `Mostrando ${filteredProducts.length} di ${products.length} prodotti` 
            : `Showing ${filteredProducts.length} of ${products.length} products`}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <div 
            className="bg-[#0a0a0a] border border-red-500/50 rounded-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={32} className="text-red-500" />
              <h2 className="text-xl font-bold">
                {language === 'it' ? 'Conferma Eliminazione' : 'Confirm Deletion'}
              </h2>
            </div>
            
            <p className="text-white/80 mb-6">
              {language === 'it' 
                ? `Sei sicuro di voler eliminare definitivamente il prodotto "${deleteConfirm.name}" (${deleteConfirm.sku})?` 
                : `Are you sure you want to permanently delete the product "${deleteConfirm.name}" (${deleteConfirm.sku})?`}
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 rounded transition-colors"
              >
                {language === 'it' ? 'Annulla' : 'Cancel'}
              </button>
              <button
                onClick={() => handleDeleteProduct(deleteConfirm.sku)}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded transition-colors font-semibold"
              >
                {language === 'it' ? 'Elimina' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Edit Product Modal */}
      {editingProduct && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#1a1a1a] border border-white/20 rounded-lg w-full max-w-4xl my-8">
            {/* Modal Header */}
            <div className="border-b border-white/10 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">
                  {language === 'it' ? 'Modifica Prodotto' : 'Edit Product'}
                </h2>
                <button
                  onClick={closeEditModal}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              <p className="text-white/60 text-sm mt-2">SKU: {editingProduct.sku}</p>
            </div>

            {/* Modal Body */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-6">
                
                {/* Images Section */}
                <div>
                  <label className="block text-sm font-semibold mb-3">
                    🖼️ {language === 'it' ? 'Immagini Prodotto' : 'Product Images'}
                  </label>
                  
                  {/* Current Images */}
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                    {editForm.images && editForm.images.map((img, index) => (
                      <div key={`img-${index}-${img.substring(0, 20)}`} className="relative group">
                        <img 
                          src={img.startsWith('data:') ? img : getProxiedImageUrl(img)}
                          alt={`Product ${index + 1}`}
                          className="w-full h-24 object-cover rounded border border-white/20"
                        />
                        <button
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Add Image by URL */}
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={imageInput}
                      onChange={(e) => setImageInput(e.target.value)}
                      placeholder={language === 'it' ? 'Incolla URL immagine...' : 'Paste image URL...'}
                      className="flex-1 bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    />
                    <button
                      onClick={handleAddImageUrl}
                      className="btn-primary px-4 py-2"
                    >
                      {language === 'it' ? 'Aggiungi' : 'Add'}
                    </button>
                  </div>

                  {/* Upload Image File */}
                  <div>
                    <label className="btn-secondary px-4 py-2 cursor-pointer inline-block">
                      <Upload size={16} className="inline mr-2" />
                      {uploadingImage ? (language === 'it' ? 'Caricamento...' : 'Uploading...') : (language === 'it' ? 'Carica da Computer' : 'Upload from Computer')}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploadingImage}
                      />
                    </label>
                    <p className="text-xs text-white/50 mt-2">
                      {language === 'it' ? 'Max 5MB, formati: JPG, PNG, WebP' : 'Max 5MB, formats: JPG, PNG, WebP'}
                    </p>
                  </div>
                </div>

                {/* Title IT/EN */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      🇮🇹 {language === 'it' ? 'Titolo Italiano' : 'Italian Title'}
                    </label>
                    <input
                      type="text"
                      value={editForm.name_it}
                      onChange={(e) => handleEditFormChange('name_it', e.target.value)}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      🇬🇧 {language === 'it' ? 'Titolo Inglese' : 'English Title'}
                    </label>
                    <input
                      type="text"
                      value={editForm.name_en}
                      onChange={(e) => handleEditFormChange('name_en', e.target.value)}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                {/* Description IT/EN */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      🇮🇹 {language === 'it' ? 'Descrizione Italiana' : 'Italian Description'}
                    </label>
                    <textarea
                      value={editForm.description_it}
                      onChange={(e) => handleEditFormChange('description_it', e.target.value)}
                      rows={6}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      🇬🇧 {language === 'it' ? 'Descrizione Inglese' : 'English Description'}
                    </label>
                    <textarea
                      value={editForm.description_en}
                      onChange={(e) => handleEditFormChange('description_en', e.target.value)}
                      rows={6}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                {/* Price, Stock, Category, Brand */}
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      💰 {language === 'it' ? 'Prezzo (€)' : 'Price (€)'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.price}
                      onChange={(e) => handleEditFormChange('price', parseFloat(e.target.value))}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      📦 {language === 'it' ? 'Stock' : 'Stock'}
                    </label>
                    <input
                      type="number"
                      value={editForm.stock}
                      onChange={(e) => handleEditFormChange('stock', parseInt(e.target.value))}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      📂 {language === 'it' ? 'Categoria' : 'Category'}
                    </label>
                    <select
                      value={editForm.category}
                      onChange={(e) => handleEditFormChange('category', e.target.value)}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    >
                      {categories.map(cat => (
                        <option key={cat.name} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      🏷️ {language === 'it' ? 'Marca' : 'Brand'}
                    </label>
                    <input
                      type="text"
                      value={editForm.brand}
                      onChange={(e) => handleEditFormChange('brand', e.target.value)}
                      className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary"
                    />
                  </div>
                </div>

                {/* Compatible Motorcycles (one per line) */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    🏍️ {language === 'it' ? 'Compatibilità Moto (una per riga)' : 'Bike Compatibility (one per line)'}
                  </label>
                  <textarea
                    value={editForm.compatible_models || ''}
                    onChange={(e) => handleEditFormChange('compatible_models', e.target.value)}
                    rows={5}
                    placeholder={language === 'it'
                      ? 'Es.\nGas Gas Mc 65 2021 - 2023\nHusqvarna Tc 65 2017 - 2023\nKtm Sx 65 1998 - 2023'
                      : 'e.g.\nGas Gas Mc 65 2021 - 2023\nHusqvarna Tc 65 2017 - 2023'}
                    className="w-full bg-black/30 border border-white/20 rounded px-4 py-2 focus:outline-none focus:border-primary font-mono text-sm"
                    data-testid="edit-compatible-models"
                  />
                  <p className="text-xs text-white/50 mt-1">
                    {language === 'it'
                      ? 'Appare come chip nella sezione "Compatibilità Moto" della scheda prodotto.'
                      : 'Displayed as chips under the "Bike Compatibility" section on the product page.'}
                  </p>
                </div>

              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-white/10 p-6 flex justify-end gap-3">
              <button
                onClick={closeEditModal}
                className="btn-secondary px-6 py-2"
              >
                {language === 'it' ? 'Annulla' : 'Cancel'}
              </button>
              <button
                onClick={saveProductChanges}
                className="btn-primary px-6 py-2"
              >
                💾 {language === 'it' ? 'Salva Modifiche' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import from URL Modal */}
      {showImportModal && (
        <ImportFromUrlModal
          onClose={() => setShowImportModal(false)}
          onSaved={() => {
            setShowImportModal(false);
            fetchProducts();
          }}
        />
      )}

      {/* Import from Supplier Modal (Acerbis, etc.) */}
      {showSupplierModal && (
        <ImportFromSupplierModal
          onClose={() => setShowSupplierModal(false)}
          onSaved={() => {
            setShowSupplierModal(false);
            fetchProducts();
          }}
        />
      )}

    </div>
  );
};

export default AdminInventory;
